/**
 * La plantilla de la página de UNA oferta, diseñada en el editor.
 *
 * Igual que la entrada del blog (`BLOG_TEMPLATE_PATH`): el editor publica UNA
 * página por tipo —producto, servicio, producto digital, reserva— con un
 * marcador en cada dato enlazado, y el worker la rellena con la oferta que se
 * pide. Sin plantilla publicada, el worker sigue pintando su página de siempre.
 *
 * Lo que no es un dato sino una pieza viva —la zona de compra, con sus tallas,
 * cantidad, envío, cupón y botón; la galería de fotos— va como un hueco
 * (`OFFER_BUY_SLOT`, `OFFER_GALLERY_SLOT`) que el worker cambia por la suya.
 * Así el editor decide DÓNDE va y cómo se viste su caja, y la lógica de cobro
 * sigue siendo una sola: la del worker.
 */
/** Los tipos con plantilla propia. Los nombres son los de las páginas del catálogo. */
export const OFFER_TEMPLATE_KINDS = ['products', 'services', 'digital', 'bookings', 'memberships'];
export function isOfferTemplateKind(value) {
    return typeof value === 'string' && OFFER_TEMPLATE_KINDS.includes(value);
}
/** Dónde la publica el editor. Como `_blog/`, nunca se sirve tal cual. */
export function offerTemplatePath(kind) {
    return `_offers/${kind}.html`;
}
/** Los datos que se enlazan. Los ids son los de los campos de oferta del editor. */
export const OFFER_TEMPLATE_FIELDS = ['title', 'description', 'image', 'price', 'duration', 'type', 'cta', 'options', 'badge'];
/** Mismo formato que los del blog: sobrevive a escapes y atributos sin cambiar. */
export function offerTemplateMarker(field) {
    return `%%OFFER:${field}%%`;
}
/** El id de la oferta inventada con la que se publica la plantilla. */
export const OFFER_TEMPLATE_ITEM_ID = 'offer-template';
// ─── Plantilla de colección ──────────────────────────────────────────────────
/**
 * La página de cada colección (`/colecciones/<slug>`), diseñada en el editor
 * como UNA plantilla para todas: igual que la de cada oferta, sale una vez con
 * marcadores y el worker la rellena con la colección que se pide. Sin ella, el
 * worker sigue pintando su página de siempre.
 */
export const COLLECTION_TEMPLATE_PATH = '_collections/template.html';
/**
 * El diseño PROPIO de una colección, si lo tiene: el worker lo busca antes que
 * el general (`COLLECTION_TEMPLATE_PATH`). El slug va limpio y con un prefijo,
 * para que ninguno se llame como el general.
 */
export function collectionTemplatePathFor(slug) {
    return `_collections/c-${slug.toLowerCase().replace(/[^a-z0-9-]/g, '-')}.html`;
}
export const COLLECTION_TEMPLATE_FIELDS = ['title', 'description', 'image', 'slug'];
export function collectionTemplateMarker(field) {
    return `%%COLLECTION:${field}%%`;
}
/** El id de la colección inventada con la que se publica la plantilla. */
export const COLLECTION_TEMPLATE_ITEM_ID = 'collection-template';
/** La plantilla con una colección dentro. Cada marcador, por su valor escapado. */
export function fillCollectionTemplate(html, values) {
    let out = html;
    for (const field of COLLECTION_TEMPLATE_FIELDS)
        out = out.split(collectionTemplateMarker(field)).join(escapeText(values[field] ?? ''));
    /* Una imagen sin foto: fuera la etiqueta entera, no un recuadro roto. */
    return out.replace(/<img\b[^>]*\ssrc=""[^>]*>/gi, '');
}
/** Los huecos de las piezas vivas. Comentarios: no se ven si algo falla. */
export const OFFER_BUY_SLOT = '<!--OFFER:BUY-->';
export const OFFER_GALLERY_SLOT = '<!--OFFER:GALLERY-->';
export const OFFER_GALLERY_STACK_SLOT = '<!--OFFER:GALLERY:STACK-->';
/** Cuántos campos puede tener un tipo. Más que esto es una ficha, no una oferta. */
export const MAX_OFFER_FIELDS = 12;
const FIELD_TYPES = ['text', 'richText', 'image'];
const FIELD_ID = /^[a-z0-9][a-z0-9-]{0,39}$/;
/** Los campos guardados, limpios: lo que no tenga forma de campo se descarta. */
export function readOfferFields(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object')
        return out;
    for (const kind of OFFER_TEMPLATE_KINDS) {
        const list = raw[kind];
        if (!Array.isArray(list))
            continue;
        const seen = new Set();
        const fields = list.flatMap((entry) => {
            if (!entry || typeof entry !== 'object')
                return [];
            const { id, label, type } = entry;
            if (typeof id !== 'string' || !FIELD_ID.test(id) || seen.has(id))
                return [];
            if (typeof label !== 'string' || !label.trim())
                return [];
            seen.add(id);
            return [{ id, label: label.trim().slice(0, 60), type: FIELD_TYPES.includes(type) ? type : 'text' }];
        }).slice(0, MAX_OFFER_FIELDS);
        if (fields.length)
            out[kind] = fields;
    }
    return out;
}
/** Los valores de una oferta: sólo textos, por id. */
export function readOfferCustomValues(raw) {
    if (!raw || typeof raw !== 'object')
        return {};
    return Object.fromEntries(Object.entries(raw)
        .filter((entry) => FIELD_ID.test(entry[0]) && typeof entry[1] === 'string' && entry[1].trim() !== ''));
}
/** Un id para un campo nuevo a partir de su nombre, sin chocar con los que ya hay. */
export function offerFieldIdFor(label, taken) {
    const base = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || 'campo';
    let id = base;
    for (let n = 2; taken.includes(id); n += 1)
        id = `${base}-${n}`;
    return id;
}
/** La clave con la que se enlaza un campo personalizado en el editor. */
export function offerCustomFieldKey(id) {
    return `custom.${id}`;
}
/** El id del campo personalizado de una clave de enlace, o `null` si no lo es. */
export function offerCustomFieldId(key) {
    return key.startsWith('custom.') ? key.slice('custom.'.length) : null;
}
/** Su marcador en la plantilla publicada. */
export function offerCustomMarker(id) {
    return `%%OFFER:custom.${id}%%`;
}
/**
 * Las marcas que deja el editor para esconder lo que una oferta no tiene: en
 * la pieza enlazada, `data-vo-field="<id>"`; en su sección, si sólo depende de
 * campos personalizados, `data-vo-needs="<id>,<id>"`. El worker añade
 * `data-vo-empty` a las que se quedan sin dato y la hoja las esconde.
 */
export const OFFER_FIELD_ATTR = 'data-vo-field';
/** Lo que pide una sección con el bloque «Información del producto» dentro. */
export const OFFER_DETAILS_NEED = '__details';
export const OFFER_NEEDS_ATTR = 'data-vo-needs';
import { OFFER_DETAILS_SLOT_PATTERN, readOfferDetailsSlot } from './offerDetails';
import { OFFER_GALLERY_COMPONENT_PATTERN, readOfferGalleryConfig } from './offerGallery';
const escapeText = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/*
 * La cabecera que puso el editor, para quitarla: la de una oferta la escribe
 * el worker con los ajustes de página de esa oferta (`offerPageHead`).
 */
const HEAD_TAGS = [
    /<title>[\s\S]*?<\/title>/gi,
    /<meta\s+name="(?:description|robots|twitter:[^"]*)"[^>]*>/gi,
    /<meta\s+property="og:[^"]*"[^>]*>/gi,
    /<link\s+rel="canonical"[^>]*>/gi,
];
/**
 * La plantilla con una oferta dentro.
 *
 * Cada marcador se cambia por su valor escapado. Una imagen sin foto se quita
 * entera: una `<img>` con el marcador por dirección sería un recuadro roto.
 */
export function fillOfferTemplate(html, values, parts) {
    let out = html;
    out = out.replace(OFFER_GALLERY_COMPONENT_PATTERN, (_slot, encoded) => parts.galleryComponent?.(readOfferGalleryConfig(encoded)) ?? '');
    const customValues = parts.custom?.values ?? {};
    /* El bloque de información, primero: si se queda vacío, su sección se
       esconde con la misma marca que un campo sin dato (`__details`). */
    let detailsShown = false;
    out = out.replace(OFFER_DETAILS_SLOT_PATTERN, (_slot, encoded) => {
        const markup = parts.details ? parts.details(readOfferDetailsSlot(encoded)) : '';
        if (markup)
            detailsShown = true;
        return markup;
    });
    const filled = (id) => (id === OFFER_DETAILS_NEED ? detailsShown : Boolean(customValues[id]?.trim()));
    /* Lo que se queda sin dato se marca —la pieza y su sección— y se esconde. */
    out = out.replace(new RegExp(`${OFFER_FIELD_ATTR}="([a-z0-9-]+)"`, 'g'), (attr, id) => (filled(id) ? attr : `${attr} data-vo-empty`));
    out = out.replace(new RegExp(`${OFFER_NEEDS_ATTR}="([a-z0-9,_-]*)"`, 'g'), (attr, ids) => (ids.split(',').filter(Boolean).some(filled) ? attr : `${attr} data-vo-empty`));
    const types = new Map((parts.custom?.fields ?? []).map((field) => [field.id, field.type]));
    out = out.replace(/%%OFFER:custom\.([a-z0-9-]+)%%/g, (_marker, id) => {
        const value = customValues[id] ?? '';
        return types.get(id) === 'richText' ? value : escapeText(value);
    });
    for (const pattern of HEAD_TAGS)
        out = out.replace(pattern, '');
    out = out.replace(/<head(\s[^>]*)?>/i, (tag) => `${tag}\n${parts.head}\n<style>[data-vo-empty]{display:none!important}</style>`);
    if (!values.image) {
        out = out.replace(new RegExp(`<img\\b[^>]*${escapeRegExp(offerTemplateMarker('image'))}[^>]*>`, 'g'), '');
    }
    for (const field of OFFER_TEMPLATE_FIELDS) {
        /* Las opciones llegan ya en marcado (`offerOptionsHtml`, que escapa lo suyo). */
        out = out.split(offerTemplateMarker(field)).join(field === 'options' ? values.options ?? '' : escapeText(values[field] ?? ''));
    }
    const gallery = (layout) => typeof parts.gallery === 'function' ? parts.gallery(layout) : parts.gallery;
    return out.split(OFFER_BUY_SLOT).join(parts.buy)
        .split(OFFER_GALLERY_SLOT).join(gallery('thumbnails'))
        .split(OFFER_GALLERY_STACK_SLOT).join(gallery('stack'));
}
//# sourceMappingURL=offerTemplate.js.map